import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';

/**
 * Shared design tokens for the "nav item" look used by both the main sidebar
 * (`NavItem`, built on `ListItemButton`) and the Settings rail (`SettingsNav`,
 * built on `Tabs`). The two render different MUI primitives — a list button vs.
 * a tab with an animated indicator — so each applies these values inside its
 * own component-specific `sx` selectors. This module is the single source of
 * truth for the *values*, not the markup, so the two can't drift apart again.
 *
 * Colors are palette tokens (resolved by MUI against the active theme), so the
 * navs stay in lockstep with the theme automatically.
 */

/**
 * Pill horizontal inset, in theme spacing units (`mx`). 1 unit = 8px. Applied
 * on the non-stripe side only; the active pill is flush (inset 0) against the
 * menu border on the stripe side.
 */
export const NAV_PILL_INSET = 1;
/** Pill corner radius, in theme shape units. */
export const NAV_PILL_RADIUS = 1.5;

/** Accent stripe — a `Box` in `NavItem`, the `Tabs` indicator in Settings. */
export const NAV_STRIPE_WIDTH = 3; // px
export const NAV_STRIPE_COLOR = 'primary.main';

/** Leading icon. */
export const NAV_ICON_MIN_WIDTH = 40; // px
export const NAV_ICON_GAP = 1.5; // theme spacing, icon → label

/** Label typography (matches the MUI `Tab` defaults so both read identically). */
export const NAV_LABEL_FONT_SIZE = '0.875rem';
export const NAV_LABEL_FONT_WEIGHT = 600;

/** Foreground (icon + label) color by state. */
export const NAV_COLOR_ACTIVE = 'text.primary';
export const NAV_COLOR_INACTIVE = 'text.secondary';

/** Surface color by state. */
export const NAV_BG_ACTIVE = 'action.selected';
export const NAV_BG_HOVER = 'action.hover';

/**
 * Background transition for hover/selection. Shared so the click/hover feedback
 * animates identically in both navs — change the timing here and both follow.
 * (The Settings `Tabs` indicator additionally *slides* between tabs via MUI; the
 * sidebar navigates/remounts so its stripe appears rather than slides — that
 * difference is structural, not styling.)
 */
export const NAV_TRANSITION_MS = 150;
export const NAV_TRANSITION = `background-color ${NAV_TRANSITION_MS}ms ease`;

/**
 * Pill geometry shared by both navs, parameterised by the side the accent
 * stripe (and the menu border) sits on. It lives in the *base* style so the
 * hover and selected backgrounds render the SAME shape — flush + squared
 * against the border on the stripe side, inset + rounded on the other — and it
 * carries the shared transition. Change the pill shape or its animation here
 * and both navs follow.
 */
export const navPillSx = (stripeSide: 'left' | 'right'): SystemStyleObject<Theme> => ({
  position: 'relative',
  borderRadius: NAV_PILL_RADIUS,
  // Flush against the menu border on the stripe side, inset on the other.
  marginLeft: stripeSide === 'left' ? 0 : NAV_PILL_INSET,
  marginRight: stripeSide === 'right' ? 0 : NAV_PILL_INSET,
  ...(stripeSide === 'left'
    ? { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }
    : { borderTopRightRadius: 0, borderBottomRightRadius: 0 }),
  transition: NAV_TRANSITION,
});
